import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskService, Task, GetTasksParams } from '@/services/taskService';
import { useToast } from '@/hooks/use-toast';
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  AlertCircle,
  Plus,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TaskFormDialog } from './TaskFormDialog';
import { format } from 'date-fns';
import SmartPagination from '@/components/ui/smart-pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TaskPanelProps {
  providerId: string;
  patientId?: string;
  clinicId?: string;
}

export const TaskPanel: React.FC<TaskPanelProps> = ({ providerId, patientId, clinicId }) => {
  const baseScopeFilters = useMemo<Pick<GetTasksParams, 'assigned_to' | 'patient_id' | 'clinic_id'>>(
    () => ({
      ...(patientId ? { patient_id: patientId } : {}),
      ...(providerId ? { assigned_to: providerId } : {}),
      ...(clinicId ? { clinic_id: clinicId } : {}),
    }),
    [patientId, providerId, clinicId]
  );

  const [filters, setFilters] = useState<GetTasksParams>({
    ...baseScopeFilters,
    page: 1,
    limit: 20,
  });
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'complete' | 'delete';
    task: Task;
  } | null>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      ...baseScopeFilters,
      page: 1,
    }));
  }, [baseScopeFilters]);

  // Fetch tasks
  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => taskService.getTasks(filters),
    staleTime: 0,
    gcTime: 0,
  });

  const { data: pendingCountData } = useQuery({
    queryKey: ['tasks-count', 'pending', baseScopeFilters],
    queryFn: () =>
      taskService.getTasks({
        ...baseScopeFilters,
        status: 'pending',
        page: 1,
        limit: 1,
      }),
  });

  const { data: overdueCountData } = useQuery({
    queryKey: ['tasks-count', 'overdue', baseScopeFilters],
    queryFn: () =>
      taskService.getTasks({
        ...baseScopeFilters,
        status: 'overdue',
        page: 1,
        limit: 1,
      }),
  });

  // Mark complete mutation
  const markCompleteMutation = useMutation({
    mutationFn: (taskId: string) => taskService.markComplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({
        title: 'Task Completed',
        description: 'Task marked as complete',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update task',
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({
        title: 'Task Deleted',
        description: 'Task has been deleted',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to delete task',
        variant: 'destructive',
      });
    },
  });

  const tasks = data?.data?.tasks || [];
  const pagination = data?.data?.pagination;
  const pendingCount = pendingCountData?.data?.pagination?.total || 0;
  const overdueCount = overdueCountData?.data?.pagination?.total || 0;

  const getTaskStatus = (task: Task) => task.effective_status || task.status;

  const getStatusIcon = (task: Task) => {
    const status = getTaskStatus(task);
    if (status === 'overdue') {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    }

    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-amber-600" />;
      default:
        return <Circle className="h-4 w-4 text-slate-400" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-300',
      medium: 'bg-amber-100 text-amber-800 border-amber-300',
      low: 'bg-slate-100 text-slate-600 border-slate-300',
    };
    return (
      <Badge variant="outline" className={colors[priority as keyof typeof colors]}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels = {
      follow_up_visit: 'Follow-up Visit',
      lab_review: 'Lab Review',
      medication_check: 'Medication Check',
      patient_checkin: 'Patient Check-in',
      documentation: 'Documentation',
      other: 'Other',
    };
    return labels[category as keyof typeof labels] || category;
  };

  const getStatusLabel = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const statusFilterLabel = filters.status ? getStatusLabel(filters.status) : 'All';
  const priorityFilterLabel = filters.priority
    ? filters.priority.charAt(0).toUpperCase() + filters.priority.slice(1)
    : 'All';

  const getStatusBadge = (task: Task) => {
    const status = getTaskStatus(task);
    if (status === 'overdue') {
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          Overdue
        </Badge>
      );
    }

    if (status === 'completed') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
          Completed
        </Badge>
      );
    }

    const colors = {
      pending: 'bg-slate-100 text-slate-700 border-slate-300',
      in_progress: 'bg-amber-100 text-amber-800 border-amber-300',
      overdue: 'bg-red-100 text-red-800 border-red-300',
    };

    return (
      <Badge variant="outline" className={colors[status as keyof typeof colors] || colors.pending}>
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const handleMarkComplete = (taskId: string) => {
    const task = tasks.find((item) => item.task_id === taskId);
    if (!task) return;
    setConfirmAction({ type: 'complete', task });
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setIsTaskFormOpen(true);
  };

  const handleDelete = (taskId: string) => {
    const task = tasks.find((item) => item.task_id === taskId);
    if (!task) return;
    setConfirmAction({ type: 'delete', task });
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'complete') {
      markCompleteMutation.mutate(confirmAction.task.task_id);
    } else {
      deleteMutation.mutate(confirmAction.task.task_id);
    }

    setConfirmAction(null);
  };

  const handleTaskFormClose = () => {
    setIsTaskFormOpen(false);
    setEditingTask(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Tasks</h2>
          <p className="text-sm text-slate-600 mt-1">
            Manage patient follow-ups and reminders
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">
              Pending: {pendingCount}
            </Badge>
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
              Overdue: {overdueCount}
            </Badge>
          </div>
        </div>
        <Button onClick={() => setIsTaskFormOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                Status: {statusFilterLabel}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={filters.status || 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: value === 'all' ? undefined : (value as GetTasksParams['status']),
                    page: 1,
                  }))
                }
              >
                <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="pending">Pending</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="overdue">Overdue</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="completed">Completed</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                Priority: {priorityFilterLabel}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Filter by Priority</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={filters.priority || 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    priority: value === 'all' ? undefined : (value as GetTasksParams['priority']),
                    page: 1,
                  }))
                }
              >
                <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="high">High</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="medium">Medium</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="low">Low</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Card>

      {/* Task List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-600">Loading tasks...</div>
        </div>
      ) : error ? (
        <Card className="p-6 text-center text-red-600">
          Failed to load tasks. Please try again.
        </Card>
      ) : tasks.length === 0 ? (
        <Card className="p-12 text-center">
          <Circle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks yet</h3>
          <p className="text-sm text-slate-600 mb-4">
            Create your first task to start tracking patient follow-ups
          </p>
          <Button onClick={() => setIsTaskFormOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Task
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <Card
              key={task.task_id}
              className={`p-4 hover:shadow-md transition-shadow ${
                getTaskStatus(task) === 'overdue' ? 'border-l-4 border-l-red-500' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-1">{getStatusIcon(task)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-900 truncate">{task.title}</h4>
                      {task.source === 'ai_generated' && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                          AI
                        </Badge>
                      )}
                      {/* {getStatusBadge(task)} */}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <UserCircle className="h-3 w-3" />
                        {[task.patient?.first_name, task.patient?.middle_name, task.patient?.last_name].filter(Boolean).join(' ')}
                      </span>
                      <span>•</span>
                      <span>Due: {format(new Date(task.due_date), 'MMM d, yyyy')}</span>
                      <span>•</span>
                      <span>{getCategoryLabel(task.category)}</span>
                    </div>
                    {task.notes && (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{task.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {getPriorityBadge(task.priority)}
                  {task.status === 'completed' && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      Completed
                    </Badge>
                  )}
                  {task.status !== 'completed' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkComplete(task.task_id)}
                        className="gap-1"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Complete
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(task)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(task.task_id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}

          {pagination?.pages > 1 && (
            <div className="flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={(page) =>
                  setFilters((prev) => ({
                    ...prev,
                    page,
                  }))
                }
                className="mx-0 justify-center sm:justify-start"
                maxVisiblePages={5}
              />
              <p className="whitespace-nowrap text-xs text-slate-500">
                Total: <span className="font-medium text-slate-700">{pagination.total}</span> Tasks
              </p>
            </div>
          )}
        </div>
      )}

      {/* Task Form Dialog */}
      <TaskFormDialog
        open={isTaskFormOpen}
        onOpenChange={handleTaskFormClose}
        providerId={providerId}
        patientId={patientId}
        editingTask={editingTask}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
          handleTaskFormClose();
        }}
      />

      <AlertDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmAction(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'complete' ? 'Mark task as completed?' : 'Delete this task?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'complete'
                ? `This will mark "${confirmAction?.task?.title || 'this task'}" as completed.`
                : `This will permanently remove "${confirmAction?.task?.title || 'this task'}" from your task list.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmAction?.type === 'complete' ? 'Mark Completed' : 'Delete Task'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
